import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Users, AlertCircle, Mail } from "lucide-react";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get(`/invites/preview/${token}`)
      .then(r => setInvite(r.data))
      .catch(e => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const onAccept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post("/invites/accept", { token });
      setAccepted(data);
      toast.success(`${data.alias} is live`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <Header />
      <div className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          {loading && <div className="text-center text-sm text-[#475569]">Loading invite…</div>}

          {error && !loading && (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm text-center" data-testid="invite-error">
              <div className="mx-auto h-12 w-12 rounded-xl bg-red-100 grid place-items-center mb-4">
                <AlertCircle className="h-6 w-6 text-red-600"/>
              </div>
              <h1 className="text-2xl font-bold text-[#0F172A]">Invite unavailable</h1>
              <p className="text-sm text-[#475569] mt-3">{error}</p>
              <Link to="/" className="inline-block mt-6 text-sm text-[#F26953] font-semibold hover:underline">Go home</Link>
            </div>
          )}

          {invite && !accepted && (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm" data-testid="invite-preview">
              <div className="mx-auto h-12 w-12 rounded-xl bg-[#FEE7E1] grid place-items-center mb-6">
                <Users className="h-6 w-6 text-[#F26953]"/>
              </div>
              <h1 className="text-2xl font-bold text-[#0F172A] text-center">You're invited</h1>
              <p className="text-sm text-[#475569] text-center mt-3 leading-relaxed">
                <strong>{invite.inviter_email}</strong> wants you to take over
              </p>
              <div className="mt-4 p-4 rounded-xl bg-[#FFF8F6] border border-[#F26953]/20 text-center">
                <div className="mono font-bold text-lg text-[#F26953]">{invite.alias}@{invite.domain}</div>
                <div className="text-xs text-[#475569] mt-2">→ forwards to <strong>{invite.invitee_email}</strong></div>
              </div>
              {invite.note && (
                <blockquote className="mt-5 border-l-2 border-[#F26953] pl-4 text-sm text-[#475569] italic">
                  "{invite.note}"
                </blockquote>
              )}
              <button onClick={onAccept} disabled={accepting} data-testid="invite-accept-btn"
                className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-[#F26953] text-white font-semibold py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60">
                {accepting ? "Activating…" : <>Accept & activate forwarding <CheckCircle2 className="h-4 w-4"/></>}
              </button>
              <p className="text-[11px] text-slate-400 text-center mt-4">
                No account required — we'll just start forwarding mail to your inbox.
              </p>
            </div>
          )}

          {accepted && (
            <div className="bg-white rounded-2xl border border-emerald-200 p-8 shadow-sm text-center" data-testid="invite-accepted">
              <div className="mx-auto h-12 w-12 rounded-xl bg-emerald-100 grid place-items-center mb-4">
                <CheckCircle2 className="h-6 w-6 text-emerald-600"/>
              </div>
              <h1 className="text-2xl font-bold text-[#0F172A]">You're all set ✅</h1>
              <p className="text-sm text-[#475569] mt-3">
                Any mail sent to <span className="mono font-semibold text-[#F26953]">{accepted.alias}</span> will forward to <strong>{accepted.forwards_to}</strong>.
              </p>
              <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-[#E2E8F0] text-left text-xs text-[#475569] space-y-1">
                <div className="font-semibold text-[#0F172A]">What's next?</div>
                <div>• Use <span className="mono">{accepted.alias}</span> on your business cards, invoices, or marketing — every reply lands in your normal inbox.</div>
                <div>• Want your own domain inbox like this? <Link to="/signup" className="text-[#F26953] font-semibold hover:underline">Start a free trial</Link>.</div>
              </div>
              <button onClick={() => nav("/")} className="mt-6 inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0F172A]">
                <Mail className="h-4 w-4"/> Back to olyinbox.com
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
